import * as _ from "lodash";

//TODO - compared to JS methods

/**
 * Reverses `array` so that the first element becomes the last, the second
 * element becomes the second to last, and so on.
 *
 * **Note:** This method mutates `array` and is based on
 * [`Array#reverse`](https://mdn.io/Array/reverse).
 *
 * @category Array
 * @returns Returns `array`.
 * @example
 *
 * var array = [1, 2, 3];
 *
 * _.reverse(array);
 * // => [3, 2, 1]
 *
 * console.log(array);
 * // => [3, 2, 1]
 */
function reverse<TList extends Array<any>>(
    array: TList,
): TList {
    return _.reverse(array);
}

/**
 * Creates a duplicate-free version of an array, using
 * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons, in which only the first occurrence of each element
 * is kept.
 *
 * @category Array
 * @param array The array to inspect.
 * @returns Returns the new duplicate free array.
 * @example
 *
 * _.uniq([2, 1, 2]);
 * // => [2, 1]
 */
function uniq<T>(
    array: Array<T> | null | undefined
): T[] {
    return _.uniq(array);
}

/**
 * This method is like _.find except that it returns the index of the first element predicate returns truthy
 * for instead of the element itself.
 *
 * @param array The array to search.
 * @param predicate The function invoked per iteration.
 * @param fromIndex The index to search from.
 * @return Returns the index of the found element, else -1.
 */
function findIndex<T>(
    array: Array<T> | null | undefined,
    predicate?: _.ListIterateeCustom<T, boolean>,
    fromIndex?: number
): number {
    return _.findIndex(array, predicate, fromIndex);
}

/**
 * Gets the index at which the first occurrence of `value` is found in `array`
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons. If `fromIndex` is negative, it's used as the offset
 * from the end of `array`.
 *
 * @category Array
 * @param array The array to search.
 * @param value The value to search for.
 * @param [fromIndex=0] The index to search from.
 * @returns Returns the index of the matched value, else `-1`.
 * @example
 *
 * _.indexOf([1, 2, 1, 2], 2);
 * // => 1
 *
 * // using `fromIndex`
 * _.indexOf([1, 2, 1, 2], 2, 2);
 * // => 3
 */
function indexOf<T>(
    array: Array<T> | null | undefined,
    value: T,
    fromIndex?: number
): number {
    return _.indexOf(array, value, fromIndex);
}

/**
 * This method is like _.indexOf except that it iterates over elements of array from right to left.
 *
 * @param array The array to search.
 * @param value The value to search for.
 * @param fromIndex The index to search from or true to perform a binary search on a sorted array.
 * @return Returns the index of the matched value, else -1.
 */
function lastIndexOf<T>(
    array: Array<T> | null | undefined,
    value: T,
    fromIndex?: true | number
): number;
function lastIndexOf(array, value, fromIndex?) {
    return _.lastIndexOf(array, value, fromIndex);
}

/**
 * This method is like _.findIndex except that it iterates over elements of collection from right to left.
 *
 * @param array The array to search.
 * @param predicate The function invoked per iteration.
 * @param fromIndex The index to search from.
 * @return Returns the index of the found element, else -1.
 */
function findLastIndex<T>(
    array: Array<T> | null | undefined,
    predicate?: _.ListIterateeCustom<T, boolean>,
    fromIndex?: number
): number {
    return _.findLastIndex(array, predicate, fromIndex);
}

/**
 * Gets the last element of array.
 *
 * @param array The array to query.
 * @return Returns the last element of array.
 */
function last<T>(array: Array<T> | null | undefined): T | undefined {
    return _.last(array);
}

/**
 * Creates an array of grouped elements, the first of which contains the first elements of the given arrays,
 * the second of which contains the second elements of the given arrays, and so on.
 *
 * @param arrays The arrays to process.
 * @return Returns the new array of grouped elements.
 */
function zip<T1, T2>(arrays1: Array<T1>, arrays2: Array<T2>): Array<[T1 | undefined, T2 | undefined]> {
    return _.zip(arrays1, arrays2);
}

/**
* Creates an array of unique values, in order, from all of the provided arrays using SameValueZero for
* equality comparisons.
*
* @param arrays The arrays to inspect.
* @return Returns the new array of combined values.
*/
function union<T>(...arrays: Array<Array<T> | null | undefined>): T[] {
    return _.union(...arrays);
}

/**
 * Creates an array of unique array values not included in the other provided arrays using SameValueZero for
 * equality comparisons.
 *
 * @param array The array to inspect.
 * @param values The arrays of values to exclude.
 * @return Returns the new array of filtered values.
 */
function difference<T>(
    array: Array<T> | null | undefined,
    ...values: Array<Array<T>>
): T[] {
    return _.difference(array, ...values);
}

/**
* Creates an array of unique values that is the symmetric difference of the provided arrays.
*
* @param arrays The arrays to inspect.
* @return Returns the new array of values.
*/
function xor<T>(...arrays: Array<Array<T> | null | undefined>): T[] {
    return _.xor(...arrays);
}

/**
 * Binary search to find an element given a sorted array.
 * @param sortedArray The sorted javascript array
 * @param element The element to find in the array
 * @param compareFunction? optional comparison function of the form function(a, b), that returns negative number if a<b, 0 if a===b, positive number if a>b
 * @returns the index of the element if it is contained in the array. Otherwise returns the negative value of the insertion point minus 1.  The insertion point is the point at which the key would be inserted in the sorted array.  So the return value is non-negative only if the element is found.  If negative, do -result - 1 to get the insertion point.
 */
const findInSortedArray = (
    sortedArray: any,
    element: any,
    compareFunction?: any
) => {
    if (!compareFunction) {
        compareFunction = (a, b) => {
            if (a > b) return 1;
            if (a < b) return -1;
            return 0;
        };
    }
    let left = 0;
    let right = sortedArray.length - 1;

    while (left <= right) {
        const middle = (left + right) >>> 1;
        const compare = compareFunction(element, sortedArray[middle]);
        if (compare < 0) {
            right = middle - 1;
        } else if (compare > 0) {
            left = middle + 1;
        } else {
            return middle; // element found
        }
    }

    return -(left + 1); // element not found
}

/**
 * Convert given array to a hash object
 * @param array The input array
 * @param key The key extractor delegate
 * @param value? Optional value extractor delegate
 * @returns hash object
 */
const toHash = <T>(array: T[], key: (x: T) => any, value?: (x: T) => any): any => {
    const hash = {};
    for (let i = 0; i < array.length; i++) {
        hash[key(array[i])] = value ? value(array[i]) : array[i];
    }
    return hash;
}

/**
 * Convert given array into a hash of arrays
 * @param array The input array
 * @param keySelector The key extractor delegate
 * @param valueSelector? Optional value extractor delegate
 * @returns hash object
 */
// TODO in phase 2 of lodash replacements
// const groupBy = <T>(array: T[], keySelector: (x: T) => any, valueSelector?: (x: T) => any): any => {
//     const group = {};
//     for (let i = 0; i < array.length; i++) {
//         const key = keySelector(array[i]);
//         let list = group[key];
//         if (!list) {
//             group[key] = list = [];
//         }
//         list.push(valueSelector ? valueSelector(array[i]) : array[i]);
//     }
//     return group;
// };
function groupBy<T>(array: T[], key: string | ((value: T) => string)): _.Dictionary<T[]> {
    return _.groupBy(array, key);
};

/**
 * Iterates over elements of an array or object and invokes callback for each element.
 * @param arrayObject an array or an object
 * @param callback the iteratee
 * @param thisObject the scope of `this`
 * @returns void, or undefined if `arrayObject` is invalid or `callback` is not a function
 */
const safeForEach = (
    arrayObject,
    callback: (item: any, index?: number, length?: number) => any,
    thisObject?
) => {
    if (!arrayObject || typeof callback != "function") {
        return;
    }
    if (arrayObject instanceof Array) {
        arrayObject.forEach((item, index, array) => {
            callback.call(thisObject, item, index, array.length);
        });
    } else {
        Object.keys(arrayObject).forEach((key, index, array) => {
            if (arrayObject.hasOwnProperty(key)) {
                callback.call(
                    thisObject,
                    arrayObject[key],
                    index,
                    array.length
                );
            }
        });
    }
};

type Many<T> = T | ReadonlyArray<T>;
/**
 * Creates a new array concatenating `array` with any additional arrays
 * and/or values.
 *
 * @category Array
 * @param array The array to concatenate.
 * @param [values] The values to concatenate.
 * @returns Returns the new concatenated array.
 * @example
 *
 * var array = [1];
 * var other = _.concat(array, 2, [3], [[4]]);
 *
 * console.log(other);
 * // => [1, 2, 3, [4]]
 *
 * console.log(array);
 * // => [1]
 */
 function concat<T>(array: Many<T>, ...values: Array<Many<T>>): T[] {
    return _.concat(array, ...values)
};

/**
 * This method is like `_.uniq` except that it accepts `iteratee` which is
 * invoked for each element in `array` to generate the criterion by which
 * uniqueness is computed. The iteratee is invoked with one argument: (value).
 *
 * @category Array
 * @param array The array to inspect.
 * @param [iteratee=_.identity] The iteratee invoked per element.
 * @returns Returns the new duplicate free array.
 * @example
 *
 * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
 * // => [2.1, 1.2]
 *
 * // using the `_.property` iteratee shorthand
 * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
 * // => [{ 'x': 1 }, { 'x': 2 }]
 */
function uniqBy<T>(
array: _.List<T> | null | undefined,
iteratee: _.ValueIteratee<T>
): T[] {
    return _.uniqBy(array, iteratee)
};


/**
 * This method is like `_.uniq` except that it accepts `comparator` which
 * is invoked to compare elements of `array`. The order of result values is
 * determined by the order they occur in the array.The comparator is invoked
 * with two arguments: (arrVal, othVal).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 * @example
 *
 * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 2 }];
 *
 * _.uniqWith(objects, _.isEqual);
 * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
 */
// function uniqWith(array, comparator) {
//     comparator = typeof comparator == 'function' ? comparator : undefined;
//     return (array && array.length) ? baseUniq(array, undefined, comparator) : [];
// }


function uniqWith<T>
(array: _.List<T> | null | undefined,
comparator?: _.Comparator<T>): T[] {
    return _.uniqWith(array, comparator)

};

/**
 * Removes all provided values from array using SameValueZero for equality comparisons.
 *
 * Note: Unlike _.without, this method mutates array.
 *
 * @param array The array to modify.
 * @param values The values to remove.
 * @return Returns array.
 */
function pull<T>(
    array: T[],
    ...values: T[]
): T[];

/**
 * @see _.pull
 */
function pull<T>(
    array: _.List<T>,
    ...values: T[]
): _.List<T>;

function pull(array, ...values) {
    return _.pull(array, ...values)
}

/**
 * Creates an array of unique `array` values not included in the other
 * provided arrays using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @category Array
 * @param [values] The arrays to inspect.
 * @param [comparator] The comparator invoked per element.
 * @returns Returns the new array of filtered values.
 * @example
 *
 * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];

* _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
* // => [{ 'x': 2, 'y': 1 }]
*/
function differenceWith<T1, T2>(
    array: _.List<T1> | null | undefined,
    values: _.List<T2>,
    comparator: _.Comparator2<T1, T2>
): T1[];

/**
 * @see _.differenceWith
 */
function differenceWith<T1, T2, T3>(
    array: _.List<T1> | null | undefined,
    values1: _.List<T2>,
    values2: _.List<T3>,
    comparator: _.Comparator2<T1, T2 | T3>
): T1[];

/**
 * @see _.differenceWith
 */
function differenceWith<T1, T2, T3, T4>(
    array: _.List<T1> | null | undefined,
    values1: _.List<T2>,
    values2: _.List<T3>,
    ...values: Array<_.List<T4> | _.Comparator2<T1, T2 | T3 | T4>>
): T1[];

/**
 * @see _.differenceWith
 */
function differenceWith<T>(
    array: _.List<T> | null | undefined,
    ...values: Array<_.List<T>>
): T[];

function differenceWith(array, ...args) {
    return _.differenceWith(array, ...args)
}

//TODO - IS THIS USED?
/**
 * Creates an array of unique `array` values not included in the other
 * provided arrays using [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @category Array
 * @param [values] The arrays to inspect.
 * @param [comparator] The comparator invoked per element.
 * @returns Returns the new array of filtered values.
 * @example
 *
 * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
 * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];

* _.intersectionWith(objects, others, _.isEqual);
* // => [{ 'x': 1, 'y': 2 }]
*/
function intersectionWith<T1, T2>(array: _.List<T1> | null | undefined, values: _.List<T2>, comparator: _.Comparator2<T1, T2>): T1[];
/**
 * @see _.intersectionWith
 */
function intersectionWith<T1, T2, T3>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, comparator: _.Comparator2<T1, T2 | T3>): T1[];
/**
 * @see _.intersectionWith
 */
function intersectionWith<T1, T2, T3, T4>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, ...values: Array<_.List<T4> | _.Comparator2<T1, T2 | T3 | T4>>): T1[];
/**
 * @see _.intersectionWith
 */
function intersectionWith<T>(array?: _.List<T> | null, ...values: Array<_.List<T> | _.Comparator2<T, never>>): T[];

function intersectionWith(array, ...args) {
    return _.intersectionWith(array, ...args)
}

//TODO - IS THIS USED?
/**
 * This method is like _.difference except that it accepts iteratee which is invoked for each element of array
 * and values to generate the criterion by which uniqueness is computed. The iteratee is invoked with one
 * argument: (value).
 *
 * @param array The array to inspect.
 * @param values The values to exclude.
 * @param iteratee The iteratee invoked per element.
 * @returns Returns the new array of filtered values.
 */
function differenceBy<T1, T2>(array: _.List<T1> | null | undefined, values: _.List<T2>, iteratee: _.ValueIteratee<T1 | T2>): T1[];
/**
 * @see _.differenceBy
 */
function differenceBy<T1, T2, T3>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, iteratee: _.ValueIteratee<T1 | T2 | T3>): T1[];
/**
 * @see _.differenceBy
 */
function differenceBy<T1, T2, T3, T4>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, values3: _.List<T4>, iteratee: _.ValueIteratee<T1 | T2 | T3 | T4>): T1[];
/**
    * @see _.differenceBy
    */
function differenceBy<T1, T2, T3, T4, T5>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, values3: _.List<T4>, values4: _.List<T5>, iteratee: _.ValueIteratee<T1 | T2 | T3 | T4 | T5>): T1[];
/**
    * @see _.differenceBy
    */
function differenceBy<T1, T2, T3, T4, T5, T6>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, values3: _.List<T4>, values4: _.List<T5>, values5: _.List<T6>, iteratee: _.ValueIteratee<T1 | T2 | T3 | T4 | T5 | T6>): T1[];
/**
    * @see _.differenceBy
    */
function differenceBy<T1, T2, T3, T4, T5, T6, T7>(array: _.List<T1> | null | undefined, values1: _.List<T2>, values2: _.List<T3>, values3: _.List<T4>, values4: _.List<T5>, values5: _.List<T6>, ...values: Array<_.List<T7> | _.ValueIteratee<T1 | T2 | T3 | T4 | T5 | T6 | T7>>): T1[];
/**
    * @see _.differenceBy
    */
function differenceBy<T>(array: _.List<T> | null | undefined, ...values: Array<_.List<T>>): T[];

function differenceBy(array, ...args) {
    return _.differenceBy(array, ...args)
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform an array value into a boolean indicating whether or not the array has at least one element.
 *
 * Example: <div data-dojo-props="visible: at('rel:', 'array').transform(transformers.hasElements)" ...>
 */
function hasElements(source) {
    return source instanceof Array && source.length > 0;
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform an array value into a boolean indicating whether or not the array has no elements (or is null).
 *
 * Example: <div data-dojo-props="visible: at('rel:', 'array').transform(transformers.hasNoElements)" ...>
 */
function hasNoElements(source) {
    return source == null || (source instanceof Array && source.length === 0);
}

/**
 * Gets the max element of array.
 *
 * @param array The array to query.
 * @return Returns the max element of array.
 */
function max<T>(array: Array<T>): T {
    return _.max(array);
}

/**
 * Gets the max element of array using an iteratee function
 * @param array The array to query.
 * @param iteratee The iteratee invoked per element.
 * @return Returns the max element of array.
 * 
 * @example
 * var objects = [{ 'n': 1 }, { 'n': 2 }];
 * 
 * _.maxBy(objects, function(o) { return o.n; });
 * // => { 'n': 2 }
 * 
 */
function maxBy<T>(
    array: Array<T>,
    iteratee?: _.ValueIteratee<T>
): T {
    return _.maxBy(array, iteratee);
}

 /**
  * Computes the minimum value of `array`. If `array` is empty or falsey
  * `undefined` is returned.
  *
  * @category Math
  * @param array The array to iterate over.
  * @returns Returns the minimum value.
  */
function min<T>(array: Array<T> | null | undefined): T | undefined {
    return _.min(array);
}

/**
 * Reduce a collection to a value which is the accumulated result of running each element in the collection through iteratee,
 * where each successive invocation is supplied the return value of the previous.
 * The iteratee is invoked with four arguments: (accumulator, value, index|key, collection).
 * @param array The array to iterate over.
 * @param iteratee The function invoked per iteration.
 * @param accumulator The initial value.
 * @return Returns the accumulated value.
 * 
 * @example
 * var array = [[0, 1], [2, 3], [4, 5]];
 * 
 * _.reduce(array, function(flattened, other) { return flattened.concat(other); }, []);
 * // => [0, 1, 2, 3, 4, 5]
 * 
 */
function reduce<T, TResult>(
    array: T[] | {[key: string]: T},
    iteratee: (accumulator: TResult, value: T, key: string | number, collection: T[] | {[key: string]: T}) => TResult,
    accumulator: TResult
  ): TResult {
    return _.reduce(array, iteratee, accumulator);
}

/**
 * Rejects all elements of an array that satisfy the predicate.
 * @param array The array to iterate over.
 * @param predicate The function invoked per iteration.
 * @return Returns the new filtered array.
 * 
 * @example
 * var users = [
 *  { 'user': 'barney', 'active': true },
 * { 'user': 'fred',   'active': false }
 * ];
 * 
 * _.reject(users, function(o) { return !o.active; });
 * // => objects for ['fred']
 * 
 */
function reject<T>(array: T[] | {[key: string]: T}, predicate: (value: T, index: number | string) => boolean): unknown[] {
    return _.reject(array, predicate);
}

/**
 * flattens array a single level deep. 
 * @param array The array to flatten.
 * @return Returns the new flattened array.
 * 
 * @example
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 * 
 */
function flatten<T>(array: _.List<Many<T>> | null | undefined): T[] {
    return _.flatten(array);
}   

/**
 * returns true if predicate returns truthy for all elements of collection.
 * @param array The array to iterate over.
 * @param predicate The function invoked per iteration.
 * @return Returns true if all elements pass the predicate check, else false.
 * 
 * @example
 * var users = [
 * { 'user': 'barney', 'age': 36 },
 * { 'user': 'fred',   'age': 40 }
 * ];
 * 
 * _.every(users, function(o) { return o.age > 18; });
 * // => true
 */
function every<T>(
    collection: _.List<T> | null | undefined,
    predicate?: _.ListIterateeCustom<T, boolean>
): boolean;
function every<T extends object>(
    collection: T | null | undefined,
    predicate?: _.ObjectIterateeCustom<T, boolean>
): boolean;
function every(collection, predicate?): boolean {
    return _.every(collection, predicate);
}

/**
 * Returns the sum of all elements after invoking iteratee for each element
 * @param array The array to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the sum of all elements.
 * 
 * @example
 * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
 * 
 * _.sumBy(objects, function(o) { return o.n; });
 * // => 20
 */
function sumBy<T>(array: T[], iteratee?: string | ((value: T) => number)): number {
    return _.sumBy(array, iteratee);
}

/**
 * Returns the sum of all elements
 * @param array The array to iterate over.
 * @return Returns the sum of all elements.
 * 
 * @example
 * _.sum([4, 2, 8, 6]);
 * // => 20
 */
function sum(array: number[] | null | undefined): number {
    return _.sum(array);
}

/**
 * Mutates the array to remove all elements from values
 * @param array The array to modify.
 * @param values The values to remove.
 * @return Returns array.
 * 
 * @example
 * var array = [1, 2, 3, 4];
 * 
 * _.pull(array, [2, 4]);
 * console.log(array);
 * // => [1, 3]
 */
function pullAll<T>(array: T[] | null | undefined, values?: T[] | null | undefined): T[] {
    return _.pullAll(array, values);
}

/**
 * Creates an array of unique values, in order, from all given arrays using iteratee for equality comparisons.
 * @param arrays The arrays to inspect.
 * @param iteratee The iteratee invoked per element.
 * @return Returns the new array of combined values.
 * 
 * @example
 * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
 * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
 * 
 * _.unionBy(objects, others, _.property('x'));
 * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
 */
function unionBy<T1, T2>(arrays1: T1[], arrays2: T2[], iteratee?: (value: T1 | T2) => any): (T1 | T2)[] {
    return _.unionBy(arrays1, arrays2, iteratee);
}

/**
 * Removes all elements from array that predicate returns truthy for and returns an array of the removed elements.
 * The predicate is invoked with three arguments: (value, index, array).
 * @param array The array to modify.
 * @param predicate The function invoked per iteration.
 * @return Returns the new array of removed elements.
 * 
 * @example
 * var array = [1, 2, 3, 4];
 * var evens = _.remove(array, function(n) {
 * return n % 2 == 0;
 * });
 * 
 * console.log(array);
 * // => [1, 3]
 */
function remove<T>(array: T[], predicate: (value: T, index: number) => boolean): T[] {
    return _.remove(array, predicate);
}

/**
 * Creates an array that is the intersection of the provided arrays.
 * @param arrays The arrays to inspect.
 * @return Returns the new array of intersecting values.
 * 
 * @example
 * _.intersection([2, 1], [2, 3]);
 * // => [2]
 */
function intersection<T>(...arrays: (T[]|_.List<T>)[]): T[] {
    return _.intersection(...arrays);
}

/**
 * Handles the join method of an array
 * @param array The array to join
 * @param separator The separator
 * 
 * @example
 * var array = ['a', 'b', 'c'];
 * 
 * _.join(array, '~');
 * // => 'a~b~c'
*/
function join(array: _.List<any>, separator?: string): string {
    return _.join(array, separator);
}

/** Returns the first element of an array
 * @param array The array to query
 * @return Returns the first element of array
 * 
 * @example
 * var array = [1, 2, 3];
 * 
 * _.first(array);
 * // => 1
 * 
 * _.first([]);
 * // => undefined
 */
function first<T>(array: T[]): T | null | undefined {
    return _.first(array);
}

/**
 * Creates an array of elements split into groups the length of size. If collection can’t be split evenly, the
 * final chunk will be the remaining elements.
 *
 * @param array The array to process.
 * @param size The length of each chunk.
 * @return Returns the new array containing chunks.
 */
function chunk<T>(array: T[] | null | undefined, size?: number): T[][] {
    return _.chunk(array, size);
}

/**
 * Creates an array with all falsey values removed. The values false, null, 0, "", undefined, and NaN are
 * falsey.
 *
 * @param array The array to compact.
 * @return Returns the new array of filtered values.
 */
function compact<T>(array: T[] | null | undefined): T[] {
    return _.compact(array);
}

export const ArrayUtil = {
    // Wrapping lodash
    reverse,
    uniq,
    findIndex,
    indexOf,
    lastIndexOf,
    findLastIndex,
    last,
    zip,
    union,
    difference,
    xor,
    // Migrated from Dayforce: UI\Framework\Scripts\Framework\Modules\ArrayUtil.ts
    findInSortedArray,
    toHash,
    groupBy,
    safeForEach,
    concat,
    uniqBy,
    uniqWith,
    pull,
    differenceWith,
    intersectionWith,
    differenceBy,
    hasElements,
    hasNoElements,
    max,
    maxBy,
    min,
    reduce,
    reject,
    flatten,
    every,
    sumBy,
    sum,
    pullAll,
    unionBy,
    remove,
    intersection,
    join,
    first,
    chunk,
    compact
};

export default ArrayUtil;
